import React, { useContext, useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useUserRolePermissions } from 'hooks';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import * as R from 'ramda';
import Chip from '@mui/material/Chip';
import CounterChip from '../components/CounterChip';
import LoadingControlDetails from './LoadingControlDetails';
import { useVerifyControlNumberQuery } from '../../customerAuthorizationsEndpoints';
import { renderInput } from '../../utils/renderUtils';
import { CreateCustomerAuthContext, initialStateStep4 } from '../Create';

const LoadingControlNumbers = () => {
  const {
    info: { controlNumbers },
    setInfo,
  } = useContext(CreateCustomerAuthContext);

  const [inputValue, setInputValue] = useState('');
  const [message, setMessage] = useState('');

  const { currentAffiliate } = useUserRolePermissions();

  const params = {
    loadingControlIdName: inputValue,
    supplier: {
      companyCustomerIdName: currentAffiliate?.customerIdName,
      dataCompanyId: currentAffiliate?.dataCompanyId,
    },
  };

  const skip = R.lt(inputValue.length, 1);

  const { data, isError, isFetching } = useVerifyControlNumberQuery({ params }, { skip });

  const handleChange = (_, __, reason) => {
    if (reason === 'clear') {
      R.compose(
        setInfo,
        R.curryN(2, R.compose(R.mergeLeft(initialStateStep4), R.assoc('controlNumbers'))),
        R.always([])
      )();
    }
  };

  const handleInputChange = (_, value) => {
    setInputValue(value);
  };

  const onDelete = value =>
    R.compose(
      setInfo,
      R.curryN(2, R.compose(R.mergeLeft(initialStateStep4), R.assoc('controlNumbers'))),
      R.always(R.reject(R.propEq('idName', value), controlNumbers))
    );

  const handleClick = () => {
    if (!isFetching && !isError) {
      if (data.doesExist) {
        setMessage(
          'This loading control number already exists. Please enter a new loading control number.'
        );
      } else if (R.gt(R.findIndex(R.propEq('idName', inputValue), controlNumbers), -1)) {
        setMessage('This loading control number is currently in the list');
      } else {
        R.compose(
          setInfo,
          R.curryN(2, R.compose(R.mergeLeft(initialStateStep4), R.assoc('controlNumbers'))),
          R.always(
            R.append(
              {
                idName: inputValue,
              },
              controlNumbers
            )
          )
        )();
        setInputValue('');
      }
    }
  };

  const renderTags = R.curry((tags, getTagProps) =>
    tags.map(item => (
      <Chip
        {...getTagProps(item)}
        key={item}
        label={item}
        onDelete={onDelete(item)}
        sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
        variant="outlined"
      />
    ))
  );

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Typography variant="subtitle1">Loading Control Numbers</Typography>
          {controlNumbers.length > 0 && <CounterChip count={controlNumbers.length} />}
        </Stack>
        <Typography component="div" sx={{ gridColumn: { lg: '1 / 3' }, gridRow: { lg: '1 / 2' } }}>
          At least one Loading Control Number must be selected for the authorization. Loading
          Control Numbers can be configured with or without a destination.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridGap: 16,
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
            mt: 2,
          }}
        >
          <Autocomplete
            freeSolo
            getOptionLabel={R.prop('idName')}
            inputValue={inputValue}
            multiple
            onChange={handleChange}
            onFocus={() => setMessage('')}
            onInputChange={handleInputChange}
            options={[]}
            renderInput={renderInput({
              label: 'Loading Control ID',
              maxLength: 14,
            })}
            renderTags={renderTags}
            value={R.pluck('idName', controlNumbers)}
          />
          <Button
            disabled={skip}
            onClick={handleClick}
            sx={{
              '&.MuiButtonBase-root.Mui-disabled': {
                backgroundColor: 'graphic.light',
              },
              alignSelf: 'center',
            }}
          >
            Add
          </Button>
          <Typography sx={{ gridColumn: { lg: '1 / 3' }, gridRow: { lg: '2 / 3' } }}>
            {message}
          </Typography>
        </Box>
        <Stack mt={2} spacing={2}>
          {controlNumbers.map(controlNumber => (
            <LoadingControlDetails controlNumber={controlNumber} key={controlNumber.idName} />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default LoadingControlNumbers;
